import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Button,
  Select,
  message,
  Form,
  Popconfirm,
  Steps,
  Upload,
  Space,
} from "antd";
import GenericTable from "../../../Components/GenericTable";
import GenericModal from "../../../Components/GenericModal";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import editIcon from "../../../Assets/Icons/editTable.svg";
import PhoneInput from "react-phone-input-2";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { getUnlinkedTrackers } from "../../../API/User/trackers";
import {
  createVehicle,
  editVehicle,
  deleteVehicle,
} from "../../../API/User/vehicle";
import { getCustomerDataById } from "../../../API/User/customer";
import { useNavigate, useLocation } from "react-router-dom";
import "./style.css";

const VehicleInformation = ({ onNext, onCancel, propCustomerId }) => {
  const [form] = Form.useForm();
  const [vehicleEditForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [vehicleOptions, setVehicleOptions] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  const customerId = propCustomerId || location.state?.customerId;

  useEffect(() => {
    if (!customerId) return;

    const fetchTrackers = async () => {
      try {
        const data = await getUnlinkedTrackers(customerId);
        const options = data.trackers.map((t) => ({
          label: t.name,
          value: t.id,
        }));
        setVehicleOptions(options);
      } catch (err) {
        messageApi.error("Failed to load trackers");
      }
    };

    fetchTrackers();
  }, [customerId]);
  const [vehicleData, setVehicleData] = useState([]);

  useEffect(() => {
    if (!customerId) return;

    const fetchExistingVehicles = async () => {
      try {
        const res = await getCustomerDataById(customerId);
        if (Math.floor(res.status / 100) === 2) {
          const customerData = res.data.customer;
          const formattedVehicles = customerData.vehicles?.map((v) => ({
            key: v.id,
            vehicle: v.vehicle_name,
            license_plate_number: v.license_plate_number,
            driver_name: v.driver_name,
            driver_contact: v.driver_contact,
            year: v.manufacturing_year,
            trackerId: v.tracker_id,
          }));
          setVehicleData(formattedVehicles || []);
        }
      } catch (err) {
        messageApi.error("Failed to load existing vehicles");
      }
    };

    fetchExistingVehicles();
  }, [customerId]);
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const handleEditVehicle = (record) => {
    setEditingVehicle(record);
    vehicleEditForm.setFieldsValue({
      licensePlate: record.license_plate_number,
      driverName: record.driver_name,
      driverContact: record.driver_contact?.replace(/^\+/, ""),
      year: record.year,
    });
    setIsVehicleModalOpen(true);
  };

  const handleVehicleModalCancel = () => {
    setIsVehicleModalOpen(false);
    setEditingVehicle(null);
    vehicleEditForm.resetFields();
  };

  const handleVehicleModalOk = async () => {
    try {
      const values = await vehicleEditForm.validateFields();
      setVehicleSaving(true);

      const res = await editVehicle({
        vehicle_id: editingVehicle.key,
        customer: customerId,
        license_plate_number: values.licensePlate,
        driver_name: values.driverName,
        driver_contact: values.driverContact?.startsWith("+")
          ? values.driverContact
          : `+${values.driverContact}`,
        manufacturing_year: parseInt(values.year, 10),
      });

      if (Math.floor(res.status / 100) === 2) {
        setVehicleData((prev) =>
          prev.map((item) =>
            item.key === editingVehicle.key
              ? {
                  ...item,
                  license_plate_number: values.licensePlate,
                  driver_name: values.driverName,
                  driver_contact: values.driverContact,
                  year: values.year,
                }
              : item,
          ),
        );
        messageApi.success("Vehicle updated successfully!");
        setIsVehicleModalOpen(false);
        setEditingVehicle(null);
        vehicleEditForm.resetFields();
      } else {
        messageApi.error("Failed to update vehicle.");
      }
    } catch (err) {
      if (err?.errorFields) return;
      const backendMsg = err.response?.data
        ? Object.values(err.response.data).flat().join(" | ")
        : "Failed to update vehicle.";
      messageApi.error(backendMsg);
    } finally {
      setVehicleSaving(false);
    }
  };

  const vehicleColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle"),
    },

    {
      title: "License Plate Number",
      dataIndex: "license_plate_number",
      key: "license_plate_number",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("license_plate_number"),
    },
    {
      title: "Driver Name",
      dataIndex: "driver_name",
      key: "driver_name",
      ...getColumnSearchProps("driver_name"),
    },
    {
      title: "Driver Number",
      dataIndex: "driver_contact",
      key: "driver_contact",
      ...getColumnSearchProps("driver_contact"),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      sorter: (a, b) => new Date(a.year) - new Date(b.year),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "13px", alignItems: "center" }}>
          <a onClick={() => handleEditVehicle(record)}>
            <img src={editIcon} alt="Edit" />
          </a>
          <Popconfirm
            title="Are you sure to delete this?"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            okText="Yes"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.key, record.trackerId)}
          >
            <a>
              <img src={delIcon} alt="Delete" />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddVehicle = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields([
        "vehicle",
        "licensePlate",
        "driver_name",
        "driver_number",
        "vehicleYear",
      ]);

      if (!customerId) {
        messageApi.error(
          "Customer ID is missing. Please create customer first.",
        );
        return;
      }

      const payload = {
        tracker_id: Number(values.vehicle),
        license_plate_number: values.licensePlate,
        manufacturing_year: parseInt(values.vehicleYear),
        driver_name: values.driver_name,
        driver_contact: values.driver_number.replace(/\D/g, ""),
        customer: customerId,
      };

      const response = await createVehicle(payload);

      const vehicleOption = vehicleOptions.find(
        (v) => v.value === values.vehicle,
      );

      const newVehicle = {
        key: response.data.id || Date.now().toString(),
        vehicle: vehicleOption?.label || values.vehicle,
        license_plate_number:
          response.data.license_plate_number || values.licensePlate,
        driver_name: response.data.driver_name || values.driver_name,
        driver_contact: response.data.driver_contact || values.driver_number,
        year: response.data.manufacturing_year || values.vehicleYear,
        trackerId: response.data.tracker_id || values.vehicle,
        customer: response.data.customer || customerId,
      };

      setVehicleData((prev) => [...prev, newVehicle]);
      messageApi.success("Vehicle added successfully!");
      form.resetFields();
    } catch (err) {
      console.error("Vehicle creation error:", err);

      if (err.response?.data) {
        const backendMsg = Object.values(err.response.data).flat().join(" | ");
        messageApi.error(backendMsg || "Failed to add vehicle.");
      } else {
        messageApi.error("Failed to add vehicle. Please check all fields.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (key, trackerId) => {
    try {
      if (trackerId) {
        const response = await deleteVehicle(trackerId, true);

        const successMsg =
          response?.data?.message || "Vehicle deleted successfully";

        messageApi.success(successMsg);
      }

      setVehicleData((prev) => prev.filter((item) => item.key !== key));
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to delete vehicle";
      messageApi.error(errorMsg);
      console.error(err);
    }
  };
  const handleNext = async () => {
    navigate("/customer-list");
  };

  const handleBack = () => {
    onCancel?.();
    navigate(-1);
  };

  return (
    <div className="customer-form">
      {contextHolder}
      <Form form={form} layout="vertical">
        <div className="add-container">
          <div className="heading">Vehicle Information</div>
          <div className="details">
            <div className="input-group">
              <div className="title">Vehicle</div>
              <Form.Item
                name="vehicle"
                rules={[{ required: true, message: "Please select a vehicle" }]}
              >
                <Select
                  showSearch
                  placeholder="Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={vehicleOptions}
                />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">License Plate Number</div>
              <Form.Item
                name="licensePlate"
                rules={[
                  {
                    required: true,
                    message: "Please enter a license plate number",
                  },
                ]}
              >
                <Input placeholder="Enter License Plate Number" />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Driver Name</div>
              <Form.Item
                name="driver_name"
                rules={[
                  { required: true, message: "Please enter driver name" },
                ]}
              >
                <Input placeholder="Enter Driver Name" />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Driver Number</div>
              <Form.Item
                name="driver_number"
                rules={[
                  { required: true, message: "Please enter driver number" },
                ]}
              >
                <PhoneInput
                  country={"jm"}
                  placeholder="Enter Contact"
                  inputClass="phone-input"
                  specialLabel={false}
                  onChange={(value) => {
                    form.setFieldsValue({
                      driver_number: value.startsWith("+")
                        ? value
                        : `+${value}`,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="input-group">
              <div className="title">Manufacturing Year</div>
              <Form.Item
                name="vehicleYear"
                rules={[
                  { required: true, message: "Please enter vehicle year" },
                  {
                    pattern: /^\d{4}$/,
                    message: "Please enter a valid 4-digit year",
                  },
                ]}
              >
                <Input
                  placeholder="Enter Manufacturing Year"
                  maxLength={4}
                  type="number"
                />
              </Form.Item>
            </div>
          </div>

          <div className="input-wrapper">
            <Form.Item>
              <Button
                type="primary"
                className="add-btn"
                onClick={handleAddVehicle}
              >
                Add
              </Button>
            </Form.Item>
          </div>
        </div>
      </Form>

      <div className="table-container">
        <div className="heading">Vehicle List</div>
        <div className="table">
          <GenericTable dataSource={vehicleData} columns={vehicleColumns} />
        </div>
      </div>

      <div className="form-actions">
        <Button onClick={handleBack}>Back</Button>
        <Button
          className="save-btn"
          type="primary"
          onClick={() => {
            handleNext();
          }}
        >
          Done
        </Button>
      </div>

      <GenericModal
        title="Edit Vehicle"
        open={isVehicleModalOpen}
        onOk={handleVehicleModalOk}
        onCancel={handleVehicleModalCancel}
        okText="Save"
        okButtonProps={{ loading: vehicleSaving }}
      >
        <Form form={vehicleEditForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="licensePlate"
            label="License Plate Number"
            rules={[
              { required: true, message: "Please enter a license plate number" },
            ]}
          >
            <Input placeholder="Enter License Plate Number" />
          </Form.Item>

          <Form.Item
            name="driverName"
            label="Driver Name"
            rules={[{ required: true, message: "Please enter driver name" }]}
          >
            <Input placeholder="Enter Driver Name" />
          </Form.Item>

          <Form.Item
            name="driverContact"
            label="Driver Number"
            rules={[{ required: true, message: "Please enter driver number" }]}
          >
            <PhoneInput
              country={"jm"}
              placeholder="Enter Contact"
              inputClass="phone-input"
              specialLabel={false}
              onChange={(value) => {
                vehicleEditForm.setFieldsValue({
                  driverContact: value.startsWith("+") ? value : `+${value}`,
                });
              }}
            />
          </Form.Item>

          <Form.Item
            name="year"
            label="Manufacturing Year"
            rules={[
              { required: true, message: "Please enter vehicle year" },
              {
                pattern: /^\d{4}$/,
                message: "Please enter a valid 4-digit year",
              },
            ]}
          >
            <Input placeholder="Enter Manufacturing Year" maxLength={4} />
          </Form.Item>
        </Form>
      </GenericModal>
    </div>
  );
};

export default VehicleInformation;
